import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../config/apiClientConfig';

const responseSchema = z.object({
	emirates: z.array(z.object({ id: z.number(), name: z.string(), isDisabled: z.boolean() })).optional(),
	genders: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	countries: z
		.array(
			z.object({ id: z.number(), name: z.string(), nationality: z.string().optional().nullable(), isGCC: z.boolean() })
		)
		.optional(),
	maritalStatuses: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	salaryBands: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	relationships: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	insuranceCompanies: z
		.array(
			z.object({
				id: z.number(),
				name: z.string(),
				logoUrl: z.string(),
				shortName: z.string(),
				paymentVendorId: z.number(),
			})
		)
		.optional(),
	plans: z
		.array(
			z.object({
				id: z.number(),
				name: z.string(),
				shortName: z.string(),
				topHospitals: z.string(),
				code: z.string(),
				insuranceCompanyId: z.number(),
				planTypeIds: z.array(z.number()),
				annualLimitCover: z.string(),
				annualLimitPharmacy: z.string(),
				coPaymentsDiagnostic: z.string(),
				coPaymentsPharmacy: z.string(),
				coPaymentsLabTests: z.string(),
				coPaymentsInPatient: z.string(),
				coPaymentsOPConsultations: z.string(),
				coPaymentsPhysioTreatment: z.string(),
				coPaymentsMaternity: z.string(),
				coPaymentsDental: z.string(),
				coPaymentsOptical: z.string(),
				policyWordingUrlEn: z.string(),
				policyWordingUrlAr: z.string(),
				isInvestorPartnerOnly: z.number(),
				benefitSummary: z.array(z.string()),
				isSponsorLaborContractRequired: z.boolean(),
			})
		)
		.optional(),
	planTypes: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	facilityTypes: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	networkGroups: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	planExclusions: z.array(z.object({ id: z.number(), description: z.string() })).optional(),
	benefitCategories: z.array(z.object({ id: z.number(), name: z.string(), shortName: z.string() })).optional(),
	benefits: z
		.array(
			z.object({
				id: z.number(),
				code: z.string(),
				benefitCategoryId: z.number(),
				name: z.string(),
				description: z.string(),
			})
		)
		.optional(),
	systemConfigurationSettings: z
		.object({
			maxAgeAllowed: z.number(),
			policyEffectiveDateThreshold: z.number(),
			policyEndDateCutoff: z.number(),
			documentRefreshIntervalTimeInInMilliSeconds: z.number(),
			documentRefreshTimeOutInMilliSeconds: z.number(),
			apiTimeOutInSeconds: z.number(),
			uploadDocumentMaxSizeInKB: z.number(),
			newBornSettings: z.object({
				maxNumberOfDays: z.number(),
				minHeight: z.number(),
				maxHeight: z.number(),
				minWeight: z.number(),
				maxWeight: z.number(),
			}),
			maximumNoOfMember: z.number(),
			maximumChildrenCount: z.number(),
			maximumSpouseCount: z.number(),
			abuDhabiHealthInsurancePaymentUrl: z.string(),
			eidFlowVersion: z.enum(['V1', 'V2']).optional(),
			paymentVendorMethodConfigurations: z
				.array(
					z.object({
						paymentVendorId: z.number(),
						paymentVendorName: z.string(),
					})
				)
				.optional(),
			telrPaymentApplePayTestModeEnabled: z.boolean().optional(),
			telrPaymentApplePayTestAccount: z.string().optional(),
		})
		.optional(),
	uploadedDocumentConfigs: z
		.array(
			z.object({
				uploadedDocumentType: z.string(),
				allowedTypes: z.string(),
				maxAllowedSizeInKb: z.number(),
			})
		)
		.optional(),
	currencies: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	paymentConfig: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
	vatConfigurations: z
		.array(
			z.object({
				id: z.number(),
				currencyId: z.number(),
				rate: z.number(),
			})
		)
		.optional(),
	userAcknowledgementTypes: z.array(z.object({ id: z.number(), name: z.string(), isActive: z.boolean() })).optional(),
	userAcknowledgements: z
		.array(
			z.object({
				id: z.number(),
				userAcknowledgementTypeId: z.number(),
				name: z.string(),
				isActive: z.boolean(),
			})
		)
		.optional(),
	policyTypes: z.array(z.object({ id: z.number(), type: z.string() })).optional(),
	planDocumentMappings: z.array(z.object({ planId: z.number(), internationalNetworkList: z.string() })).optional(),
	occupations: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	planCategorys: z
		.array(
			z.object({
				id: z.number(),
				name: z.string(),
			})
		)
		.optional(),
	sponsorOccupations: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
	investorOrPartnerImpacts: z.array(z.object({ value: z.number(), type: z.string() })).optional(),
});

const getAppInitAPI = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/app/init',
		responseSchema,
	});

export type CustomerWebSiteAppInitResponse = TypeOfAPIResponse<ReturnType<typeof getAppInitAPI>>;

export type CustomerWebSiteAppInitError = TypeOfAPIError<ReturnType<typeof getAppInitAPI>>;

export default getAppInitAPI;
